<template>
  <b-card body-class="p-0" class="container-card-talento m-0">
    <div class="container-main-image" @mouseover="mouseHover" @mouseleave="mouseLeave">
      <b-badge pill class="badge-network" v-if="proposal.followers_count">
        <b-img :src="require('@/assets/images/icons/followers.png')" class="badge-img"></b-img>
        <span class="ml-04 followers-inner-badge">{{ getTotalInfluencers(proposal.followers_count) }}</span>
      </b-badge>

      <div class="height-310">
        <b-img
          v-if="currentImage"
          class="container-img image-animation-zoom"
          :src="currentImage"
          @error="errorLoad"
        />
        <div v-else class="container-img placeholder-img d-flex align-items-center justify-content-center">
          <feather-icon icon="ImageIcon" size="40" class="text-muted" />
        </div>

        <div class="actions-overlay">
          <b-button variant="light" size="sm" class="btn-icon btn-action mr-50" @click.stop="viewProposal">
            <feather-icon icon="EyeIcon" class="icon-action" />
          </b-button>
          <b-button variant="light" size="sm" class="btn-icon btn-action" @click.stop="editProposal">
            <feather-icon icon="Edit2Icon" class="icon-action" />
          </b-button>
          <b-button
            v-if="proposal.agency"
            v-b-tooltip.hover.left
            title="Desvincular propuesta"
            variant="light"
            size="sm"
            class="btn-icon btn-action"
            :disabled="unlinking"
            @click.stop="unlinkProposal"
          >
            <b-spinner v-if="unlinking" small />
            <feather-icon v-else icon="UserXIcon" class="icon-action text-danger" />
          </b-button>
        </div>

        <div class="data-user-proposal">
          <b-avatar
            :src="proposal.user && proposal.user.avatar ? verifyAsset(proposal.user.avatar) : ''"
            size="2.8rem"
            class="mr-0"
          />
          <div class="container-username-category">
            <div class="d-flex align-items-center">
              <span class="avenir-bold text-bold-username-proposal">
                {{ proposal.user ? proposal.user.first_name + ' ' + proposal.user.last_name : '—' }}
              </span>
              <b-badge
                v-if="proposal.is_pro"
                pill
                class="ml-50 badge-pro"
              >
                <span class="size-text">PRO</span>
              </b-badge>
            </div>
            <span class="font-tiny">{{ countryName }}</span>
          </div>
        </div>
      </div>
    </div>

    <div class="template-footer">
      <div class="container-price">
        <span class="text-network">{{ $t('creator.basePrice') }}:</span>
        <span class="text-price avenir-bold text-dark">{{ getPriceProposals(proposal.deals && proposal.deals[0]) }}</span>
      </div>
      <div class="container-footer">
        <span class="text-overflow">{{ proposal.description }}</span>
      </div>
      <div class="agency-tag mt-50" v-if="proposal.agency">
        <feather-icon icon="BriefcaseIcon" size="12" class="text-muted mr-25" />
        <span class="text-muted" style="font-size: 11px;">{{ proposal.agency.name }}</span>
      </div>
    </div>
  </b-card>
</template>

<script>
import { BBadge, BCard, BImg, BAvatar, BButton, BSpinner, VBTooltip } from 'bootstrap-vue'
import { verifyAsset } from '@/libs/utils/urls'
import { isVideo } from '@/libs/utils/formats'
import { getTotalInfluencers, getPriceProposals } from '@/domain/proposal/proposal.domain'
import talentosService from '@/services/talentos'

export default {
  name: 'TalentosCard',
  components: { BBadge, BCard, BImg, BAvatar, BButton, BSpinner },
  directives: { 'b-tooltip': VBTooltip },
  props: {
    proposal: { type: Object, required: true },
    countries: { type: Array, default: () => [] },
  },
  data() {
    return {
      verifyAsset,
      currentImage: '',
      main_interval: null,
      index_image: 0,
      show_animation: false,
      first_time_transition: false,
      unlinking: false,
    }
  },
  computed: {
    countryName() {
      const code = this.proposal.user && this.proposal.user.country
      if (!code || !this.countries.length) return ''
      const found = this.countries.find(c => c['alpha-2'] === code.toUpperCase())
      return found ? found.name : ''
    },
    // Videos without a generated preview can't be rendered in <b-img>, so they are skipped
    displayImages() {
      const images = this.proposal.images || []
      return [...images]
        .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
        .filter(img => img.media && (!isVideo(img.media) || (img.preview && img.preview.length > 0)))
        .map(img => verifyAsset(isVideo(img.media) ? img.preview : img.media))
    },
  },
  created() {
    this.setInitialImage()
  },
  methods: {
    getTotalInfluencers,
    getPriceProposals,
    setInitialImage() {
      this.currentImage = this.displayImages.length > 0 ? this.displayImages[0] : ''
    },
    mouseHover() {
      if (!this.show_animation && !this.main_interval && this.displayImages.length > 1) {
        this.show_animation = true
        this.main_interval = setInterval(() => {
          this.index_image = (this.index_image + 1) % this.displayImages.length
          this.currentImage = this.displayImages[this.index_image]
        }, 4000)
      }
    },
    mouseLeave() {
      setTimeout(() => {
        this.show_animation = false
        this.index_image = 0
        clearInterval(this.main_interval)
        this.main_interval = null
        this.setInitialImage()
      }, 10)
    },
    errorLoad() {
      this.currentImage = require('@/assets/images/dashboard/imgDefault.png')
    },
    viewProposal() {
      if (this.proposal.user) {
        this.$router.push({ name: 'view_proposals', params: { vanity_name: this.proposal.user.vanity_name } })
      }
    },
    editProposal() {
      if (!this.proposal.agency) {
        this.$router.push({ name: 'view_proposals_edit' })
      } else {
        this.$router.push({ name: 'edit_proposal', params: { vanity_name: this.proposal.user.vanity_name } })
      }
    },
    async unlinkProposal() {
      if (this.unlinking) return
      const confirmed = await this.$bvModal.msgBoxConfirm(
        '¿Seguro que quieres desvincular esta propuesta de la agencia?',
        {
          title: 'Desvincular propuesta',
          okVariant: 'danger',
          okTitle: 'Desvincular',
          cancelTitle: 'Cancelar',
          centered: true,
        }
      )
      if (!confirmed) return

      this.unlinking = true
      try {
        await talentosService.unlinkProposal(this.proposal.uuid)
        this.$bvToast.toast('La propuesta fue desvinculada correctamente.', {
          title: 'Desvinculada',
          variant: 'success',
          solid: true,
        })
        this.$emit('unlinked', this.proposal.uuid)
      } catch (e) {
        this.$bvToast.toast('No se pudo desvincular la propuesta, intenta de nuevo.', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.unlinking = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
.container-card-talento {
  cursor: default;
  border-radius: 1em;
  box-shadow: none !important;
  overflow: hidden;
}
.container-main-image {
  overflow: hidden;
  border-radius: 1em;
  position: relative;
}
.height-310 {
  background-color: #ebebeb;
  width: 100%;
  padding-bottom: calc(310 / 265 * 100%);
  position: relative;
}
.container-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  position: absolute;
  left: 0;
  top: 0;
}
.placeholder-img {
  background-color: #e0e0e0;
}
.image-animation-zoom {
  transition: transform 4s;
  transform-origin: center;
}
.container-card-talento:hover .image-animation-zoom {
  transform: scale(1.05);
}
.badge-network {
  position: absolute;
  top: 1em;
  left: 1em;
  color: white;
  padding: 0.5em;
  display: flex;
  align-items: center;
  z-index: 10;
  background: var(--primary-for-blue-cases, #863FF7);
  .badge-img {
    width: 18px;
    height: 18px;
  }
}
.ml-04 { margin-left: 0.4em; }
.followers-inner-badge {
  font-size: 12px;
  line-height: 15px;
  font-family: 'avenir-bold';
}
.actions-overlay {
  display: none;
  position: absolute;
  top: 1em;
  right: 1em;
  flex-direction: column;
  z-index: 10;
}
.container-card-talento:hover .actions-overlay {
  display: flex !important;
  animation: slideIn 300ms forwards;
}
@keyframes slideIn {
  from { transform: translateX(40px); opacity: 0; }
  to   { transform: translateX(0);    opacity: 1; }
}
.btn-action {
  background-color: white;
  border-radius: 10em;
  padding: 0.5em;
  margin-bottom: 0.5em;
  box-shadow: 0 2px 6px rgba(0,0,0,0.15);
  border: none;
}
.icon-action {
  width: 17px !important;
  height: 17px !important;
}
.data-user-proposal {
  position: absolute;
  width: 89%;
  left: 1em;
  bottom: 1em;
  display: flex;
  align-items: center;
  background-color: rgba(0,0,0,0.65);
  border-radius: 10em;
  padding: 0.6em;
  z-index: 2;
}
.container-username-category {
  margin-left: 10px;
  font-size: 12px;
  color: white;
  display: flex;
  flex-direction: column;
  width: 70%;
}
.text-bold-username-proposal {
  display: block;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.badge-pro {
  font-size: 8.5px;
  text-transform: uppercase;
  color: white;
  background-color: #902875 !important;
}
.size-text { font-size: 8.5px; text-transform: uppercase; }
.font-tiny { font-family: 'avenir-light'; font-weight: 500; }
.template-footer {
  padding: 7px;
  background-color: white;
  border-bottom-left-radius: 1em;
  border-bottom-right-radius: 1em;
}
.container-price {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.text-network {
  font-size: 10px;
  text-transform: uppercase;
  color: var(--primary-for-blue-cases, #55a6c4);
}
.text-price { font-size: 14px; }
.text-overflow {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  color: #212529;
  font-family: 'avenir-medium';
  font-size: .99em;
}
.agency-tag {
  display: flex;
  align-items: center;
}
</style>
